import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api, type AdminSubscriptionTariff, type OrderManagementSettings, type OrderManagementMode, type CatalogImportSettings, type FeatureFlags, type AuthSettings, type WooppaySettings } from '@/services/api';
import { FileSpreadsheet, Upload, Download, Trash2, Loader2, FileDown, FileText, Crown, Smartphone, Package, Flag, KeyRound, CreditCard, type LucideIcon } from 'lucide-react';

export type ClientSettingsTab = 'orders' | 'tariffs' | 'yandex-guide' | 'yandex-template' | 'catalog-import' | 'debug-feature' | 'auth' | 'payment';

const TABS: ReadonlyArray<{ key: ClientSettingsTab; label: string; icon: LucideIcon }> = [
  { key: 'orders', label: 'Управление заказами', icon: Smartphone },
  { key: 'tariffs', label: 'Тарифы Pro', icon: Crown },
  { key: 'yandex-guide', label: 'Документация Яндекс.Еда', icon: FileText },
  { key: 'yandex-template', label: 'Шаблон Excel', icon: FileSpreadsheet },
  { key: 'catalog-import', label: 'Импорт каталога', icon: Package },
  { key: 'debug-feature', label: 'Debug & Feature', icon: Flag },
  { key: 'auth', label: 'Авторизация', icon: KeyRound },
  { key: 'payment', label: 'Оплата', icon: CreditCard },
] as const;

const TAB_KEYS: ReadonlyArray<ClientSettingsTab> = TABS.map((t) => t.key);

export function isClientSettingsTab(value: string | null | undefined): value is ClientSettingsTab {
  return value != null && (TAB_KEYS as ReadonlyArray<string>).includes(value);
}

const AUTOFILL_OPTIONS = [
  { value: '', label: 'Нет' },
  { value: 'store_name', label: 'Название магазина' },
  { value: 'store_address', label: 'Адрес магазина' },
  { value: 'store_phone', label: 'Телефон' },
  { value: 'store_email', label: 'Email' },
  { value: 'store_code', label: 'Код магазина' },
  { value: 'store_website', label: 'Сайт' },
  { value: 'store_id', label: 'Store Id' },
];

type FieldConfig = { key: string; title: string; hintText?: string | null; autofill?: string | null; hidden?: boolean; required?: boolean };

export function ClientSettings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: ClientSettingsTab = isClientSettingsTab(tabParam) ? tabParam : 'orders';
  const setActiveTab = (next: ClientSettingsTab) => {
    setSearchParams(
      (prev) => {
        prev.set('tab', next);
        return prev;
      },
      { replace: true },
    );
  };

  const [yandexGuideContent, setYandexGuideContent] = useState('');
  const [yandexGuideLoading, setYandexGuideLoading] = useState(true);
  const [yandexGuideSaving, setYandexGuideSaving] = useState(false);
  const [meta, setMeta] = useState<{ exists: boolean; filename?: string; uploadedAt?: string } | null>(null);
  const [fields, setFields] = useState<FieldConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSavingFields, setIsSavingFields] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [subscriptionTariffs, setSubscriptionTariffs] = useState<AdminSubscriptionTariff[]>([]);
  const [subscriptionTariffsLoading, setSubscriptionTariffsLoading] = useState(true);
  const [subscriptionTariffsSaving, setSubscriptionTariffsSaving] = useState(false);
  const [subscriptionTariffsError, setSubscriptionTariffsError] = useState<string | null>(null);
  const [orderManagement, setOrderManagement] = useState<OrderManagementSettings>({
    mode: 'web',
    playStoreUrl: '',
    appStoreUrl: '',
  });
  const [orderManagementLoading, setOrderManagementLoading] = useState(true);
  const [orderManagementSaving, setOrderManagementSaving] = useState(false);
  const [orderManagementError, setOrderManagementError] = useState<string | null>(null);
  const [catalogImport, setCatalogImport] = useState<CatalogImportSettings>({ templateUrl: '', sampleUrl: '' });
  const [catalogImportLoading, setCatalogImportLoading] = useState(true);
  const [catalogImportSaving, setCatalogImportSaving] = useState(false);
  const [catalogImportMsg, setCatalogImportMsg] = useState<string | null>(null);
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>({
    enableTokenLogin: false,
    enableEmailLogin: false,
    showDeliveryZonesStep: false,
    showContactInfoStep: false,
    showPaymentSystemsStep: false,
    showEmployeesMenu: false,
    isDebug: false,
  });
  const [featureFlagsLoading, setFeatureFlagsLoading] = useState(true);
  const [featureFlagsSaving, setFeatureFlagsSaving] = useState(false);
  const [featureFlagsMsg, setFeatureFlagsMsg] = useState<string | null>(null);
  const [authSettings, setAuthSettings] = useState<AuthSettings>({ googleClientId: '', googleClientScope: '' });
  const [authLoading, setAuthLoading] = useState(true);
  const [authSaving, setAuthSaving] = useState(false);
  const [authMsg, setAuthMsg] = useState<string | null>(null);
  const [wooppaySettings, setWooppaySettings] = useState<WooppaySettings>({
    enabled: false,
    merchantId: '',
    terminalId: '',
    certId: '',
    privateKey: '',
    publicKey: '',
    finalizationDelay: 0,
    apiUrl: '',
    webhookBaseUrl: '',
  });
  const [wooppayLoading, setWooppayLoading] = useState(true);
  const [wooppaySaving, setWooppaySaving] = useState(false);
  const [wooppayMsg, setWooppayMsg] = useState<string | null>(null);

  const CONFIRM_DELETE_WORD = 'удалить';

  const loadMeta = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await api.getYandexInfoTemplateMeta();
      setMeta(data);
    } catch (err) {
      console.error('Failed to load template meta:', err);
      setMeta({ exists: false });
    } finally {
      setIsLoading(false);
    }
  };

  const loadFields = async () => {
    try {
      const { fields: f } = await api.getYandexInfoFields();
      setFields(Array.isArray(f) ? f : []);
    } catch {
      setFields([]);
    }
  };

  const loadYandexGuide = async () => {
    setYandexGuideLoading(true);
    try {
      const { content } = await api.getYandexIntegrationGuide();
      setYandexGuideContent(content || '');
    } catch {
      setYandexGuideContent('');
    } finally {
      setYandexGuideLoading(false);
    }
  };

  const loadSubscriptionTariffs = async () => {
    setSubscriptionTariffsLoading(true);
    setSubscriptionTariffsError(null);
    try {
      const { tariffs } = await api.getSubscriptionTariffs();
      setSubscriptionTariffs(
        tariffs.length
          ? tariffs
          : [
              { code: 'pro_1m', title: '1 месяц', durationMonths: 1, priceKzt: 0, sortOrder: 1, isEnabled: true },
              { code: 'pro_6m', title: '6 месяцев', durationMonths: 6, priceKzt: 0, sortOrder: 2, isEnabled: true },
              { code: 'pro_12m', title: '1 год', durationMonths: 12, priceKzt: 0, sortOrder: 3, isEnabled: true },
            ]
      );
    } catch (err) {
      console.error('Failed to load subscription tariffs:', err);
      setSubscriptionTariffs([]);
      setSubscriptionTariffsError('Не удалось загрузить тарифы');
    } finally {
      setSubscriptionTariffsLoading(false);
    }
  };

  const handleSubscriptionTariffChange = (
    index: number,
    updates: Partial<AdminSubscriptionTariff>
  ) => {
    setSubscriptionTariffs((prev) => prev.map((t, i) => (i === index ? { ...t, ...updates } : t)));
  };

  const handleSaveSubscriptionTariffs = async () => {
    setSubscriptionTariffsSaving(true);
    setSubscriptionTariffsError(null);
    try {
      const { tariffs } = await api.putSubscriptionTariffs(subscriptionTariffs);
      setSubscriptionTariffs(tariffs);
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
        : null;
      setSubscriptionTariffsError(msg || 'Не удалось сохранить тарифы');
    } finally {
      setSubscriptionTariffsSaving(false);
    }
  };

  const loadOrderManagement = async () => {
    setOrderManagementLoading(true);
    setOrderManagementError(null);
    try {
      const settings = await api.getOrderManagementSettings();
      setOrderManagement({
        mode: settings.mode === 'mobile' ? 'mobile' : 'web',
        playStoreUrl: settings.playStoreUrl || '',
        appStoreUrl: settings.appStoreUrl || '',
      });
    } catch (err) {
      console.error('Failed to load order management settings:', err);
      setOrderManagementError('Не удалось загрузить настройки управления заказами');
    } finally {
      setOrderManagementLoading(false);
    }
  };

  const handleOrderManagementChange = (updates: Partial<OrderManagementSettings>) => {
    setOrderManagement((prev) => ({ ...prev, ...updates }));
  };

  const handleSaveOrderManagement = async () => {
    setOrderManagementError(null);
    const invalidFormat = (u: string) => u.trim() && !/^https?:\/\//i.test(u.trim());
    if (invalidFormat(orderManagement.playStoreUrl)) {
      setOrderManagementError('Ссылка Google Play должна начинаться с https://');
      return;
    }
    if (invalidFormat(orderManagement.appStoreUrl)) {
      setOrderManagementError('Ссылка App Store должна начинаться с https://');
      return;
    }
    setOrderManagementSaving(true);
    try {
      const payload: OrderManagementSettings = {
        mode: orderManagement.mode,
        playStoreUrl: orderManagement.playStoreUrl.trim(),
        appStoreUrl: orderManagement.appStoreUrl.trim(),
      };
      const updated = await api.putOrderManagementSettings(payload);
      setOrderManagement(updated);
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
        : null;
      setOrderManagementError(msg || 'Не удалось сохранить настройки');
    } finally {
      setOrderManagementSaving(false);
    }
  };

  const loadCatalogImport = async () => {
    setCatalogImportLoading(true);
    try {
      const s = await api.getCatalogImportSettings();
      setCatalogImport(s);
    } catch (err) {
      console.error('Failed to load catalog import settings:', err);
    } finally {
      setCatalogImportLoading(false);
    }
  };

  const loadFeatureFlags = async () => {
    setFeatureFlagsLoading(true);
    try {
      const f = await api.getFeatureFlags();
      setFeatureFlags(f);
    } catch (err) {
      console.error('Failed to load feature flags:', err);
    } finally {
      setFeatureFlagsLoading(false);
    }
  };

  const handleSaveCatalogImport = async () => {
    setCatalogImportSaving(true);
    setCatalogImportMsg(null);
    try {
      const res = await api.updateCatalogImportSettings(catalogImport);
      setCatalogImport(res.settings);
      setCatalogImportMsg('Сохранено');
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
        : null;
      setCatalogImportMsg(msg || 'Ошибка сохранения');
    } finally {
      setCatalogImportSaving(false);
    }
  };

  const handleSaveFeatureFlags = async () => {
    setFeatureFlagsSaving(true);
    setFeatureFlagsMsg(null);
    try {
      const res = await api.updateFeatureFlags(featureFlags);
      setFeatureFlags(res.settings);
      setFeatureFlagsMsg('Сохранено');
    } catch {
      setFeatureFlagsMsg('Ошибка сохранения');
    } finally {
      setFeatureFlagsSaving(false);
    }
  };

  const loadAuthSettings = async () => {
    setAuthLoading(true);
    try {
      const s = await api.getAuthSettings();
      setAuthSettings(s);
    } catch (err) {
      console.error('Failed to load auth settings:', err);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleSaveAuth = async () => {
    setAuthSaving(true);
    setAuthMsg(null);
    try {
      const res = await api.updateAuthSettings(authSettings);
      setAuthSettings(res.settings);
      setAuthMsg('Сохранено');
    } catch {
      setAuthMsg('Ошибка сохранения');
    } finally {
      setAuthSaving(false);
    }
  };

  const loadWooppaySettings = async () => {
    setWooppayLoading(true);
    try {
      const s = await api.getWooppaySettings();
      setWooppaySettings(s);
    } catch (err) {
      console.error('Failed to load wooppay settings:', err);
    } finally {
      setWooppayLoading(false);
    }
  };

  const handleSaveWooppay = async () => {
    setWooppaySaving(true);
    setWooppayMsg(null);
    try {
      const updated = await api.updateWooppaySettings(wooppaySettings);
      setWooppaySettings(updated);
      setWooppayMsg('Сохранено');
    } catch {
      setWooppayMsg('Ошибка сохранения');
    } finally {
      setWooppaySaving(false);
    }
  };

  useEffect(() => {
    loadMeta();
    loadYandexGuide();
    loadSubscriptionTariffs();
    loadOrderManagement();
    loadCatalogImport();
    loadFeatureFlags();
    loadAuthSettings();
    loadWooppaySettings();
  }, []);

  useEffect(() => {
    if (meta?.exists) loadFields();
    else setFields([]);
  }, [meta?.exists]);

  const handleSaveYandexGuide = async () => {
    setYandexGuideSaving(true);
    setError(null);
    try {
      await api.putYandexIntegrationGuide(yandexGuideContent);
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
        : 'Ошибка сохранения';
      setError(msg || 'Не удалось сохранить документ');
    } finally {
      setYandexGuideSaving(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    if (ext !== '.xlsx' && ext !== '.xls') {
      setError('Только файлы Excel (.xlsx, .xls) разрешены');
      return;
    }

    setIsUploading(true);
    setError(null);
    try {
      await api.uploadYandexInfoTemplate(file);
      await loadMeta();
      await loadFields();
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
        : 'Ошибка загрузки';
      setError(msg || 'Ошибка загрузки файла');
    } finally {
      setIsUploading(false);
      e.target.value = '';
    }
  };

  const handleDownload = async () => {
    if (!meta?.exists) return;
    try {
      const blob = await api.downloadYandexInfoTemplate();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'yandex-info-template.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download:', err);
      setError('Не удалось скачать шаблон');
    }
  };

  const handleDeleteClick = () => {
    if (!meta?.exists) return;
    setShowDeleteConfirm(true);
    setDeleteConfirmInput('');
  };

  const handleDeleteConfirmClose = () => {
    setShowDeleteConfirm(false);
    setDeleteConfirmInput('');
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirmInput.trim().toLowerCase() !== CONFIRM_DELETE_WORD) return;

    setIsDeleting(true);
    setError(null);
    setShowDeleteConfirm(false);
    setDeleteConfirmInput('');
    try {
      await api.deleteYandexInfoTemplate();
      await loadMeta();
      setFields([]);
    } catch (err) {
      console.error('Failed to delete:', err);
      setError('Не удалось удалить шаблон');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDownloadDefault = async () => {
    try {
      const blob = await api.downloadDefaultYandexInfoTemplate();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'yandex-info-template-default.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download default:', err);
      setError('Не удалось скачать образец');
    }
  };

  const handleFieldChange = (index: number, updates: Partial<FieldConfig>) => {
    setFields((prev) =>
      prev.map((f, i) => (i === index ? { ...f, ...updates } : f))
    );
  };

  const handleSaveFields = async () => {
    if (fields.length === 0) return;
    setIsSavingFields(true);
    setError(null);
    try {
      await api.putYandexInfoFields(fields);
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
        : 'Ошибка сохранения';
      setError(msg || 'Не удалось сохранить');
    } finally {
      setIsSavingFields(false);
    }
  };

  return (
    <div className="container mx-auto px-6 py-6 space-y-6">
      <div
        role="tablist"
        aria-label="Разделы клиентских настроек"
        className="flex flex-wrap gap-1 border-b border-border"
      >
        {TABS.map((t) => {
          const Icon = t.icon;
          const isActive = activeTab === t.key;
          return (
            <button
              key={t.key}
              role="tab"
              type="button"
              aria-selected={isActive}
              data-testid={`tab-${t.key}`}
              onClick={() => setActiveTab(t.key)}
              className={
                'inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ' +
                (isActive
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border')
              }
            >
              <Icon className="w-4 h-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'orders' && (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-blue-500" />
            Управление заказами
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Определяет, как пользователи магазина управляют заказами в PWA.
            При выборе «Через мобильное приложение» на странице заказов скрываются кнопки действий
            и при клике по заказу показывается модалка со ссылками на скачивание приложения.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {orderManagementLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              Загрузка настроек...
            </div>
          ) : (
            <>
              {orderManagementError && (
                <p className="text-sm text-destructive" data-testid="order-management-error">
                  {orderManagementError}
                </p>
              )}
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="order-management-mode"
                    value="web"
                    checked={orderManagement.mode === 'web'}
                    onChange={() => handleOrderManagementChange({ mode: 'web' as OrderManagementMode })}
                    className="rounded-full border-input"
                  />
                  <span className="text-sm">Через сайт (в PWA доступны кнопки действий и страница деталей заказа)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="order-management-mode"
                    value="mobile"
                    checked={orderManagement.mode === 'mobile'}
                    onChange={() => handleOrderManagementChange({ mode: 'mobile' as OrderManagementMode })}
                    className="rounded-full border-input"
                  />
                  <span className="text-sm">Через мобильное приложение (в PWA открывается модалка со ссылками на приложение)</span>
                </label>
              </div>

              {orderManagement.mode === 'mobile' && (
                <div className="space-y-3 rounded-lg border p-4 bg-muted/30">
                  <p className="text-xs text-muted-foreground">
                    Оставьте поле пустым, если приложение ещё не опубликовано в соответствующем сторе — в PWA такая кнопка просто не будет показана.
                  </p>
                  <div className="space-y-2">
                    <Label htmlFor="play-store-url">Ссылка Google Play (необязательно)</Label>
                    <Input
                      id="play-store-url"
                      placeholder="https://play.google.com/store/apps/details?id=..."
                      value={orderManagement.playStoreUrl}
                      onChange={(e) => handleOrderManagementChange({ playStoreUrl: e.target.value })}
                      className="h-9"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="app-store-url">Ссылка App Store (необязательно)</Label>
                    <Input
                      id="app-store-url"
                      placeholder="https://apps.apple.com/app/..."
                      value={orderManagement.appStoreUrl}
                      onChange={(e) => handleOrderManagementChange({ appStoreUrl: e.target.value })}
                      className="h-9"
                    />
                  </div>
                </div>
              )}

              <Button onClick={handleSaveOrderManagement} disabled={orderManagementSaving}>
                {orderManagementSaving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Сохранить настройки управления заказами
              </Button>
            </>
          )}
        </CardContent>
      </Card>
      )}

      {activeTab === 'tariffs' && (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Crown className="w-5 h-5 text-amber-500" />
            Тарифы Pro-подписки
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Цены и длительности Pro-подписок, которые видят пользователи в PWA.
            Изменения сохраняются немедленно.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {subscriptionTariffsLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              Загрузка тарифов...
            </div>
          ) : (
            <>
              {subscriptionTariffsError && (
                <p className="text-sm text-destructive">{subscriptionTariffsError}</p>
              )}
              <div className="space-y-3">
                <div className="grid grid-cols-[100px_1fr_120px_100px_100px] gap-2 items-center text-xs font-medium text-muted-foreground">
                  <span>Код</span>
                  <span>Название</span>
                  <span>Цена (₸)</span>
                  <span>Мес.</span>
                  <span>Активен</span>
                </div>
                {subscriptionTariffs.map((t, i) => (
                  <div
                    key={t.code}
                    className="grid grid-cols-[100px_1fr_120px_100px_100px] gap-2 items-center"
                    data-testid={`subscription-tariff-row-${t.code}`}
                  >
                    <span className="text-xs font-mono text-muted-foreground">{t.code}</span>
                    <Input
                      value={t.title}
                      onChange={(e) => handleSubscriptionTariffChange(i, { title: e.target.value })}
                      className="h-9"
                    />
                    <Input
                      type="number"
                      min={0}
                      value={t.priceKzt}
                      onChange={(e) =>
                        handleSubscriptionTariffChange(i, {
                          priceKzt: Math.max(0, parseInt(e.target.value, 10) || 0),
                        })
                      }
                      className="h-9"
                    />
                    <Input
                      type="number"
                      min={1}
                      value={t.durationMonths}
                      onChange={(e) =>
                        handleSubscriptionTariffChange(i, {
                          durationMonths: Math.max(1, parseInt(e.target.value, 10) || 1),
                        })
                      }
                      className="h-9"
                    />
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={t.isEnabled !== false}
                        onChange={(e) => handleSubscriptionTariffChange(i, { isEnabled: e.target.checked })}
                        className="rounded border-input"
                      />
                      <span className="text-sm text-muted-foreground">Вкл.</span>
                    </label>
                  </div>
                ))}
              </div>
              <Button onClick={handleSaveSubscriptionTariffs} disabled={subscriptionTariffsSaving}>
                {subscriptionTariffsSaving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Сохранить тарифы
              </Button>
            </>
          )}
        </CardContent>
      </Card>
      )}

      {activeTab === 'yandex-guide' && (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Документация по подключению Магазина к Яндекс.Еда
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Содержимое этого документа отображается пользователям на странице «Поддержка» (wiki) при нажатии кнопки «Документация по подключению Магазина к Яндекс.Еда». Поддерживается Markdown.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {yandexGuideLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              Загрузка...
            </div>
          ) : (
            <>
              <textarea
                value={yandexGuideContent}
                onChange={(e) => setYandexGuideContent(e.target.value)}
                placeholder="Введите текст документа в формате Markdown..."
                className="min-h-[300px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                spellCheck={false}
              />
              <Button onClick={handleSaveYandexGuide} disabled={yandexGuideSaving}>
                {yandexGuideSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Сохранить документ
              </Button>
            </>
          )}
        </CardContent>
      </Card>
      )}

      {activeTab === 'yandex-template' && (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" />
            Шаблон Excel для шага «Инфо для Яндекс»
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Загруженный шаблон будет использоваться в PWA при скачивании файла в шаге «Инфо для Яндекс».
            Колонки шаблона (например &lt;Field1&gt;, &lt;Field2&gt;) определяют поля формы. Названия полей и автозаполнение настраиваются ниже.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}

          <div className="flex flex-wrap gap-2 items-center">
            <Button variant="outline" size="sm" onClick={handleDownloadDefault}>
              <FileDown className="w-4 h-4 mr-2" />
              Скачать образец шаблона
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              className="hidden"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
            >
              {isUploading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              Загрузить шаблон
            </Button>
          </div>

          {isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              Загрузка...
            </div>
          ) : meta?.exists ? (
            <div className="rounded-lg border p-4 space-y-2">
              <p className="text-sm font-medium">Текущий шаблон</p>
              <p className="text-sm text-muted-foreground">
                {meta.filename} • загружен{' '}
                {meta.uploadedAt
                  ? new Date(meta.uploadedAt).toLocaleString('ru-RU', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : '—'}
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleDownload}>
                  <Download className="w-4 h-4 mr-1" />
                  Скачать
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDeleteClick}
                  disabled={isDeleting}
                  className="text-destructive hover:text-destructive"
                >
                  {isDeleting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
                  Удалить
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Шаблон не загружен. Клиенты используют стандартную генерацию Excel.
            </p>
          )}

          {meta?.exists && fields.length > 0 && (
            <div className="rounded-lg border p-4 space-y-4 mt-4">
              <p className="text-sm font-medium">Настройка полей формы</p>
              <p className="text-xs text-muted-foreground">
                Title — отображаемое название для клиента. Подсказка — текст под полем в форме. Автозаполнение — значение из профиля магазина.
              </p>
              <div className="space-y-3">
                {fields.map((f, i) => (
                  <div key={f.key} className="space-y-2">
                    <div className="flex flex-wrap gap-2 items-center">
                      <span className="text-xs font-mono text-muted-foreground w-24 shrink-0">{f.key}</span>
                      <div className="flex-1 min-w-[120px]">
                        <Label className="sr-only">Название поля</Label>
                        <Input
                          placeholder="Название поля"
                          value={f.title}
                          onChange={(e) => handleFieldChange(i, { title: e.target.value })}
                          className="h-9"
                        />
                      </div>
                      <div className="flex-1 min-w-[140px]">
                        <Input
                          placeholder="Подсказка (необязательно)"
                          value={f.hintText ?? ''}
                          onChange={(e) => handleFieldChange(i, { hintText: e.target.value || null })}
                          className="h-9"
                        />
                      </div>
                      <select
                      className="h-9 rounded-md border border-input bg-background px-3 text-sm min-w-[160px]"
                      value={f.autofill ?? ''}
                      onChange={(e) => handleFieldChange(i, { autofill: e.target.value || null })}
                    >
                      {AUTOFILL_OPTIONS.map((o) => (
                        <option key={String(o.value)} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                    <label className="flex items-center gap-2 shrink-0 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!f.required}
                        onChange={(e) => handleFieldChange(i, { required: e.target.checked })}
                        className="rounded border-input"
                      />
                      <span className="text-sm text-muted-foreground">Обязательно</span>
                    </label>
                    <label className="flex items-center gap-2 shrink-0 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!f.hidden}
                        onChange={(e) => handleFieldChange(i, { hidden: e.target.checked })}
                        className="rounded border-input"
                      />
                      <span className="text-sm text-muted-foreground">Не отображать</span>
                    </label>
                    </div>
                  </div>
                ))}
              </div>
              <Button onClick={handleSaveFields} disabled={isSavingFields}>
                {isSavingFields ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Сохранить
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {activeTab === 'catalog-import' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              Импорт каталога
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              URL шаблонов для модалки «Импорт товаров» в PWA.
            </p>
          </CardHeader>
          <CardContent>
            {catalogImportLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <div className="space-y-4 max-w-2xl">
                <div className="space-y-1">
                  <Label>VITE_CATALOG_IMPORT_TEMPLATE_URL</Label>
                  <Input
                    value={catalogImport.templateUrl}
                    onChange={(e) => setCatalogImport({ ...catalogImport, templateUrl: e.target.value })}
                    placeholder="https://pimmer.dev/dist/files/import_catalog.xlsx"
                  />
                  <p className="text-xs text-muted-foreground">Excel-шаблон для импорта каталога.</p>
                </div>
                <div className="space-y-1">
                  <Label>VITE_CATALOG_IMPORT_SAMPLE_URL</Label>
                  <Input
                    value={catalogImport.sampleUrl}
                    onChange={(e) => setCatalogImport({ ...catalogImport, sampleUrl: e.target.value })}
                    placeholder="https://yastatic.net/.../sample.zip"
                  />
                  <p className="text-xs text-muted-foreground">YML-пример (опционально).</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button onClick={handleSaveCatalogImport} disabled={catalogImportSaving}>
                    {catalogImportSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Сохранить
                  </Button>
                  {catalogImportMsg && <span className="text-xs text-muted-foreground">{catalogImportMsg}</span>}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 'debug-feature' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Flag className="w-5 h-5" />
              Debug & Feature flags
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Флаги, которые раньше задавались через VITE_*-переменные в .env. Применяются к PWA после
              перезагрузки страницы (кэш 60 сек.).
            </p>
          </CardHeader>
          <CardContent>
            {featureFlagsLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <div className="space-y-3 max-w-2xl">
                {([
                  ['enableTokenLogin', 'Включить вход по токену (VITE_ENABLE_TOKEN_LOGIN)'],
                  ['enableEmailLogin', 'Включить вход по email/паролю (VITE_ENABLE_EMAIL_LOGIN)'],
                  ['showDeliveryZonesStep', 'Шаг «Зоны доставки» в онбординге (VITE_SHOW_DELIVERY_ZONES_STEP)'],
                  ['showContactInfoStep', 'Шаг «Контактная информация» в онбординге (VITE_SHOW_CONTACT_INFO_STEP)'],
                  ['showPaymentSystemsStep', 'Шаг «Платёжные системы» в онбординге (VITE_SHOW_PAYMENT_SYSTEMS_STEP)'],
                  ['showEmployeesMenu', 'Меню «Сотрудники» (VITE_SHOW_EMPLOYEES_MENU)'],
                  ['isDebug', 'Debug режим (IS_DEBUG)'],
                ] as ReadonlyArray<readonly [keyof FeatureFlags, string]>).map(([key, label]) => (
                  <div key={key} className="flex items-center gap-2">
                    <input
                      id={`ff-${key}`}
                      type="checkbox"
                      className="h-4 w-4"
                      checked={featureFlags[key]}
                      onChange={(e) => setFeatureFlags({ ...featureFlags, [key]: e.target.checked })}
                    />
                    <Label htmlFor={`ff-${key}`} className="cursor-pointer">{label}</Label>
                  </div>
                ))}
                <div className="flex items-center gap-2 pt-2">
                  <Button onClick={handleSaveFeatureFlags} disabled={featureFlagsSaving}>
                    {featureFlagsSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Сохранить
                  </Button>
                  {featureFlagsMsg && <span className="text-xs text-muted-foreground">{featureFlagsMsg}</span>}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 'auth' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5" />
              Авторизация
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Google OAuth client для входа в PWA (раньше задавалось через VITE-переменные).
            </p>
          </CardHeader>
          <CardContent>
            {authLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <div className="space-y-4 max-w-2xl">
                <div className="space-y-1">
                  <Label>VITE_GOOGLE_CLIENT_ID</Label>
                  <Input
                    value={authSettings.googleClientId}
                    onChange={(e) => setAuthSettings({ ...authSettings, googleClientId: e.target.value })}
                    placeholder="1027234929718-0q1ek9stvsn0bn48pjqie8hcd0ar0nb8.apps.googleusercontent.com"
                  />
                </div>
                <div className="space-y-1">
                  <Label>VITE_GOOGLE_CLIENT_SCOPE</Label>
                  <Input
                    value={authSettings.googleClientScope}
                    onChange={(e) => setAuthSettings({ ...authSettings, googleClientScope: e.target.value })}
                    placeholder="https://www.googleapis.com/auth/userinfo.profile https://www.googleapis.com/auth/userinfo.email"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Button onClick={handleSaveAuth} disabled={authSaving}>
                    {authSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Сохранить
                  </Button>
                  {authMsg && <span className="text-xs text-muted-foreground">{authMsg}</span>}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 'payment' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-emerald-500" />
              Оплата (Wooppay)
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Настройки интеграции с платёжной системой Wooppay для приёма оплаты за подписку в PWA.
            </p>
          </CardHeader>
          <CardContent>
            {wooppayLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                Загрузка настроек...
              </div>
            ) : (
              <div className="space-y-4 max-w-2xl">
                <div className="flex items-center gap-2">
                  <input
                    id="wooppay-enabled"
                    type="checkbox"
                    className="h-4 w-4"
                    checked={wooppaySettings.enabled}
                    onChange={(e) => setWooppaySettings({ ...wooppaySettings, enabled: e.target.checked })}
                  />
                  <Label htmlFor="wooppay-enabled" className="cursor-pointer">Активность (включить оплату через Wooppay)</Label>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="wooppay-merchant-id">ID мерчанта</Label>
                  <Input
                    id="wooppay-merchant-id"
                    value={wooppaySettings.merchantId}
                    onChange={(e) => setWooppaySettings({ ...wooppaySettings, merchantId: e.target.value })}
                    placeholder="Например: 12345"
                    className="h-9"
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="wooppay-terminal-id">ID терминала</Label>
                  <Input
                    id="wooppay-terminal-id"
                    value={wooppaySettings.terminalId}
                    onChange={(e) => setWooppaySettings({ ...wooppaySettings, terminalId: e.target.value })}
                    placeholder="Например: term_001"
                    className="h-9"
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="wooppay-cert-id">ID сертификата</Label>
                  <Input
                    id="wooppay-cert-id"
                    value={wooppaySettings.certId}
                    onChange={(e) => setWooppaySettings({ ...wooppaySettings, certId: e.target.value })}
                    placeholder="Например: cert_001"
                    className="h-9"
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="wooppay-private-key">Секретный ключ (RSA private key)</Label>
                  <textarea
                    id="wooppay-private-key"
                    value={wooppaySettings.privateKey}
                    onChange={(e) => setWooppaySettings({ ...wooppaySettings, privateKey: e.target.value })}
                    placeholder="-----BEGIN RSA PRIVATE KEY-----&#10;...&#10;-----END RSA PRIVATE KEY-----"
                    rows={6}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    spellCheck={false}
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="wooppay-public-key">Публичный ключ (RSA public key)</Label>
                  <textarea
                    id="wooppay-public-key"
                    value={wooppaySettings.publicKey}
                    onChange={(e) => setWooppaySettings({ ...wooppaySettings, publicKey: e.target.value })}
                    placeholder="-----BEGIN PUBLIC KEY-----&#10;...&#10;-----END PUBLIC KEY-----"
                    rows={4}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    spellCheck={false}
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="wooppay-api-url">URL API Wooppay</Label>
                  <Input
                    id="wooppay-api-url"
                    value={wooppaySettings.apiUrl}
                    onChange={(e) => setWooppaySettings({ ...wooppaySettings, apiUrl: e.target.value })}
                    placeholder="https://api.wooppay.com"
                    className="h-9"
                  />
                  <p className="text-xs text-muted-foreground">Базовый URL Wooppay API (например https://api.wooppay.com)</p>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="wooppay-webhook-base-url">Публичный URL сервера (для webhook)</Label>
                  <Input
                    id="wooppay-webhook-base-url"
                    value={wooppaySettings.webhookBaseUrl}
                    onChange={(e) => setWooppaySettings({ ...wooppaySettings, webhookBaseUrl: e.target.value })}
                    placeholder="https://your-domain.com"
                    className="h-9"
                  />
                  <p className="text-xs text-muted-foreground">Wooppay будет присылать уведомления на {wooppaySettings.webhookBaseUrl || 'https://your-domain.com'}/api/webhook/wooppay</p>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="wooppay-finalization-delay">Задержка финализации оплаты (секунды)</Label>
                  <Input
                    id="wooppay-finalization-delay"
                    type="number"
                    min={0}
                    value={wooppaySettings.finalizationDelay}
                    onChange={(e) =>
                      setWooppaySettings({
                        ...wooppaySettings,
                        finalizationDelay: Math.max(0, parseInt(e.target.value, 10) || 0),
                      })
                    }
                    placeholder="0"
                    className="h-9 w-40"
                  />
                  <p className="text-xs text-muted-foreground">
                    Время ожидания (в секундах) после успешной оплаты перед активацией подписки. 0 — активировать немедленно.
                  </p>
                </div>

                <div className="flex items-center gap-2 pt-2">
                  <Button onClick={handleSaveWooppay} disabled={wooppaySaving}>
                    {wooppaySaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Сохранить настройки оплаты
                  </Button>
                  {wooppayMsg && (
                    <span className="text-xs text-muted-foreground">{wooppayMsg}</span>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-background rounded-lg border shadow-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-2">Подтверждение удаления</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Вы уверены, что хотите удалить текущий шаблон? Клиенты будут использовать стандартную генерацию Excel.
              Для подтверждения введите слово <strong>удалить</strong>.
            </p>
            <Input
              placeholder="Введите «удалить»"
              value={deleteConfirmInput}
              onChange={(e) => setDeleteConfirmInput(e.target.value)}
              className="mb-4"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={handleDeleteConfirmClose}>
                Отмена
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteConfirm}
                disabled={deleteConfirmInput.trim().toLowerCase() !== CONFIRM_DELETE_WORD}
              >
                Удалить шаблон
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
